import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import {
  deserialize,
  serialize,
  type KnownPreferences,
  type PreferenceKey,
  type UserPreferenceRow,
} from './types';

const PREFS_QUERY_KEY = ['user-preferences', 'all'] as const;

/**
 * #1714 — user preferences are session-stable (theme/density/etc. change
 * only when the user explicitly flips a setting, and every such flip
 * invalidates `PREFS_QUERY_KEY` via the mutation's `onSettled`). A short
 * stale window caused a per-route refetch storm (28× across 17 pages in
 * the 2026-06-25 nav benchmark) that helped trip the 429 rate limiter.
 * 5 min stale → navigation serves from cache; explicit edits stay live
 * because they invalidate.
 */
const PREFS_STALE_TIME = 5 * 60 * 1000;

interface PreferencesListResponse {
  items: UserPreferenceRow[];
}

/**
 * Eager-prefetch every preference for the current user/org. Mounted once
 * in the app shell via `<UserPreferencesProvider>`. Subsequent
 * `useUserPreference()` calls read from this single TanStack Query cache.
 */
export function useUserPreferencesPrefetch(): void {
  const { isAuthenticated, getAccessToken } = useAuth();
  useQuery({
    queryKey: PREFS_QUERY_KEY,
    queryFn: () =>
      apiFetch<PreferencesListResponse>('/api/v1/user-preferences', {
        getAccessToken,
      }),
    enabled: isAuthenticated,
    staleTime: PREFS_STALE_TIME,
  });
}

/**
 * Typed hook for a single preference. Behaves like `useState` — returns
 * `[value, setValue]` where `setValue` performs an optimistic update + PUT
 * with rollback on error.
 *
 * For known keys (declared in `types.ts`), the value is typed; for any
 * other key it falls through as `string`.
 */
export function useUserPreference<K extends keyof KnownPreferences>(
  key: K,
  fallback: KnownPreferences[K],
): [KnownPreferences[K], (next: KnownPreferences[K]) => Promise<void>];
export function useUserPreference<T extends string>(
  key: string,
  fallback: T,
): [T, (next: T) => Promise<void>];

export function useUserPreference(
  key: PreferenceKey,
  fallback: unknown,
): [unknown, (next: unknown) => Promise<void>] {
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuth();

  const list = useQuery({
    queryKey: PREFS_QUERY_KEY,
    queryFn: () =>
      apiFetch<PreferencesListResponse>('/api/v1/user-preferences', {
        getAccessToken,
      }),
    staleTime: PREFS_STALE_TIME,
  });

  const value = (() => {
    const raw = list.data?.items?.find((r) => r.key === key)?.value;
    return deserialize(key as keyof KnownPreferences, raw, fallback as never);
  })();

  const mutation = useMutation({
    mutationFn: (next: unknown) =>
      apiFetch<UserPreferenceRow>(`/api/v1/user-preferences/${encodeURIComponent(key)}`, {
        method: 'PUT',
        getAccessToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: serialize(next) }),
      }),
    onMutate: async (next: unknown) => {
      await queryClient.cancelQueries({ queryKey: PREFS_QUERY_KEY });
      const previous = queryClient.getQueryData<PreferencesListResponse>(PREFS_QUERY_KEY);
      const optimistic = applyOptimisticPref(previous, key, serialize(next));
      queryClient.setQueryData(PREFS_QUERY_KEY, optimistic);
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(PREFS_QUERY_KEY, ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: PREFS_QUERY_KEY });
    },
  });

  const setValue = useCallback(
    async (next: unknown): Promise<void> => {
      await mutation.mutateAsync(next);
    },
    [mutation],
  );

  return [value, setValue];
}

function applyOptimisticPref(
  current: PreferencesListResponse | undefined,
  key: string,
  rawValue: string,
): PreferencesListResponse {
  const items = current?.items ?? [];
  const idx = items.findIndex((r) => r.key === key);
  const nowIso = new Date().toISOString();
  if (idx >= 0) {
    const next = [...items];
    next[idx] = { ...next[idx]!, value: rawValue, updatedAt: nowIso };
    return { items: next };
  }
  return {
    items: [
      ...items,
      {
        id: `optimistic-${key}`,
        userId: 'me',
        organizationId: '',
        key,
        value: rawValue,
        updatedAt: nowIso,
      },
    ],
  };
}
